import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {MoneyField, useSplitTabsTableContainerRef} from '@/components/common';
import {daysBetween, formatDayMonYYYY, parseDateFlexible} from '@/utils/dataUtils';
import {
    getCashflow,
    saveCashflowLastYear,
    type CashflowLine,
    type CashflowVO,
} from '@/services/auditService/cashflowService';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {resolveCashflowNavFsCode} from '@/pages/components/fs-report/cashflowNavMap';

export interface CashflowProps {
    companyId: number;
    annualId: number;
    /** Adjustment tabs stay mounted. Refetch when this tab is shown so new journals appear. */
    active?: boolean;
    /** Open related FS schedule/WP (same path as BS/PL schedule chip). */
    onOpenFsCode?: (fsCode: string) => void;
}

function isZero(n: number | null | undefined): boolean {
    return !n || Math.abs(Number(n)) < 0.005;
}

function shouldShow(line: CashflowLine, hideLastYear: boolean, lyDraft: Record<string, number>): boolean {
    if (line.header) return true;
    if (line.total) return true;
    if (!line.hideIfZero) return true;
    const ty = Number(line.thisYear ?? 0) || 0;
    const lyRaw = lyDraft[line.code] !== undefined
        ? lyDraft[line.code]
        : (Number(line.lastYear ?? 0) || 0);
    const ly = hideLastYear ? 0 : lyRaw;
    return !isZero(ty) || !isZero(ly);
}

export const Cashflow: React.FC<CashflowProps> = ({
    companyId,
    annualId,
    active = true,
    onOpenFsCode,
}) => {
    const tableContainerRef = useSplitTabsTableContainerRef();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saveMsg, setSaveMsg] = useState<string | null>(null);
    const [data, setData] = useState<CashflowVO | null>(null);
    /** Local Last year edits (code → amount); flushed on Save */
    const [lyDraft, setLyDraft] = useState<Record<string, number>>({});
    const lyDirtyRef = useRef(false);

    const annual = useCompanyAnnualStore((s) => s.byAnnualId[Number(annualId)]);
    const periodFrom = data?.periodFrom || annual?.periodFrom || null;
    const periodTo = data?.periodTo || annual?.periodTo || null;
    const currency = data?.presentationCurrency || annual?.presentationCurrency || 'HKD';
    const companyName = data?.companyName || '';

    const periodLineText = useMemo(() => {
        if (!periodFrom || !periodTo) return '';
        const days = daysBetween(periodFrom, periodTo);
        const isYear = days === 365 || days === 366;
        const periodToDate = parseDateFlexible(periodTo);
        const periodFromDate = parseDateFlexible(periodFrom);
        if (isYear) {
            return 'For the year ended ' + formatDayMonYYYY(periodToDate);
        }
        return (
            'For the period from '
            + formatDayMonYYYY(periodFromDate)
            + ' to '
            + formatDayMonYYYY(periodToDate)
        );
    }, [periodFrom, periodTo]);

    const hydrateLyDraft = useCallback((vo: CashflowVO | null) => {
        const next: Record<string, number> = {};
        for (const line of vo?.lines || []) {
            if (!line.code || line.header) continue;
            next[line.code] = Number(line.lastYear ?? 0) || 0;
        }
        setLyDraft(next);
        lyDirtyRef.current = false;
    }, []);

    useEffect(() => {
        let cancelled = false;
        const cid = Number(companyId) || 0;
        const aid = Number(annualId) || 0;
        if (!cid || !aid || !active) {
            return;
        }
        setLoading(true);
        setError(null);
        setSaveMsg(null);
        (async () => {
            try {
                const vo = await getCashflow(cid, aid);
                if (!cancelled) {
                    setData(vo);
                    hydrateLyDraft(vo);
                }
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.message || 'Failed to load cash flow statement');
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, annualId, active, hydrateLyDraft]);

    const hideLastYear = Boolean(data?.hideLastYear);
    const lines = useMemo(
        () => (data?.lines || []).filter((l) => shouldShow(l, hideLastYear, lyDraft)),
        [data?.lines, hideLastYear, lyDraft],
    );

    const variance = Number(data?.varianceThisYear ?? 0) || 0;
    const showVarianceWarn = !isZero(variance);

    const handleOpenSchedule = useCallback((line: CashflowLine) => {
        if (!onOpenFsCode) return;
        const fsCode = resolveCashflowNavFsCode(line.code, {
            header: line.header,
            total: line.total,
            source: line.source,
        });
        if (!fsCode) return;
        onOpenFsCode(fsCode);
    }, [onOpenFsCode]);

    const handleLyChange = useCallback((code: string, n: number) => {
        setLyDraft((prev) => ({...prev, [code]: n}));
        lyDirtyRef.current = true;
        setSaveMsg(null);
    }, []);

    const handleSaveLastYear = useCallback(async () => {
        const cid = Number(companyId) || 0;
        const aid = Number(annualId) || 0;
        if (!cid || !aid) return;
        setSaving(true);
        setError(null);
        setSaveMsg(null);
        try {
            const vo = await saveCashflowLastYear(cid, aid, lyDraft);
            setData(vo);
            hydrateLyDraft(vo);
            setSaveMsg('Last year amounts saved');
        } catch (e: any) {
            setError(e?.message || 'Failed to save Last year amounts');
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, lyDraft, hydrateLyDraft]);

    if (loading && !data) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2}}>
                <CircularProgress size={28}/>
            </Box>
        );
    }

    if (error && !data) {
        return (
            <Box sx={{p: 2}}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{px: 1.5, pt: 1, pb: 0.5, flexShrink: 0}}
            >
                <Box sx={{minWidth: 0}}>
                    {!!companyName && (
                        <Typography variant="h6" fontWeight={700} mb={0.5}>{companyName}</Typography>
                    )}
                    {!!periodLineText && (
                        <Typography variant="subtitle2" mb={0.25}>{periodLineText}</Typography>
                    )}
                    <Typography variant="subtitle1" fontWeight={700} mb={0.25}>
                        Statement of cash flows
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" mb={0.25}>
                        (For management information purpose only)
                    </Typography>
                    {!hideLastYear && (
                        <Typography variant="caption" color="text.secondary">
                            Last year column is editable — type comparative amounts, then Save.
                        </Typography>
                    )}
                </Box>
                {!hideLastYear && (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={14} color="inherit"/> : <SaveIcon/>}
                        disabled={saving || loading}
                        onClick={() => { void handleSaveLastYear(); }}
                        sx={{textTransform: 'none', flexShrink: 0}}
                    >
                        Save Last year
                    </Button>
                )}
            </Stack>

            {showVarianceWarn && (
                <Alert severity="warning" sx={{mx: 1.5, mb: 0.5, py: 0}}>
                    Cash flow closing vs BS (cash − overdraft) variance: {variance.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}
                </Alert>
            )}
            {error && data && (
                <Alert severity="error" sx={{mx: 1.5, mb: 0.5}} onClose={() => setError(null)}>{error}</Alert>
            )}
            {saveMsg && (
                <Alert severity="success" sx={{mx: 1.5, mb: 0.5}} onClose={() => setSaveMsg(null)}>{saveMsg}</Alert>
            )}

            <TableContainer
                ref={tableContainerRef}
                component={Paper}
                elevation={0}
                sx={{flex: 1, minHeight: 0, overflow: 'auto', borderRadius: 0}}
            >
                <Table size="small" stickyHeader sx={{minWidth: hideLastYear ? 480 : 640}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight: 700, minWidth: 320}}>Item</TableCell>
                            <TableCell align="right" sx={{fontWeight: 700, width: 140}}>
                                {currency ? `This year (${currency})` : 'This year'}
                            </TableCell>
                            {!hideLastYear && (
                                <TableCell align="right" sx={{fontWeight: 700, width: 160}}>
                                    {currency ? `Last year (${currency})` : 'Last year'}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {lines.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={hideLastYear ? 2 : 3}>
                                    <Typography variant="body2" color="text.secondary">
                                        No cash flow lines
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            lines.map((line) => {
                                const indent = Number(line.indent ?? 0) || 0;
                                const isHeader = Boolean(line.header);
                                const isTotal = Boolean(line.total);
                                const fontWeight = isHeader || isTotal ? 700 : 400;
                                const pl = 1 + indent * 2;
                                const navFsCode = resolveCashflowNavFsCode(line.code, {
                                    header: line.header,
                                    total: line.total,
                                    source: line.source,
                                });
                                const clickable = Boolean(navFsCode && onOpenFsCode && !isHeader);
                                const lyValue = lyDraft[line.code] !== undefined
                                    ? lyDraft[line.code]
                                    : (Number(line.lastYear ?? 0) || 0);
                                return (
                                    <TableRow
                                        key={line.code}
                                        sx={{
                                            bgcolor: isTotal ? 'action.hover' : undefined,
                                            '& td': {borderBottomColor: isHeader ? 'divider' : undefined},
                                        }}
                                    >
                                        <TableCell sx={{fontWeight, pl, py: 0.75}}>
                                            {line.label}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                py: 0.5,
                                                ...(clickable ? {
                                                    cursor: 'pointer',
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                    textUnderlineOffset: 2,
                                                    '&:hover': {opacity: 0.85},
                                                } : {}),
                                            }}
                                            onClick={clickable ? () => handleOpenSchedule(line) : undefined}
                                            title={clickable && navFsCode ? `Open ${navFsCode}` : undefined}
                                        >
                                            {isHeader ? null : (
                                                <MoneyField
                                                    value={Number(line.thisYear ?? 0) || 0}
                                                    editable={false}
                                                    showCents
                                                    align="right"
                                                    currencySymbol=""
                                                />
                                            )}
                                        </TableCell>
                                        {!hideLastYear && (
                                            <TableCell
                                                align="right"
                                                sx={{py: 0.5}}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {isHeader ? null : (
                                                    <MoneyField
                                                        value={lyValue}
                                                        editable
                                                        showCents
                                                        align="right"
                                                        currencySymbol=""
                                                        onChangeNumber={(n) => handleLyChange(line.code, n)}
                                                    />
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Cashflow;
